import React from 'react';
import { Chip, Grid, Skeleton, Stack, Typography } from '@mui/material';
import { ArrowDown, ArrowUp } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import MainCard from '../../components/MainCard';
import { calculatePeriodDelta, PeriodDelta } from './stats-report';

interface DeltaBadgeProps {
  delta: PeriodDelta;
  /** Expenses read better inverted — a drop in spend is the "good" direction. */
  invertColor?: boolean;
}

const DeltaBadge: React.FC<DeltaBadgeProps> = ({ delta, invertColor = false }) => {
  if (delta.deltaPercentage === null) return null;
  const isGood = invertColor ? !delta.isPositive : delta.isPositive;
  return (
    <Chip
      variant="combined"
      color={isGood ? 'success' : 'error'}
      icon={delta.isPositive ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
      label={`${Math.abs(delta.deltaPercentage)}%`}
      size="small"
      sx={{ ml: 1, pl: 1, borderRadius: 1 }}
    />
  );
};

interface ComparisonSummaryCardProps {
  label: string;
  amountA: number;
  amountB: number;
  currency: string;
  labelA: string;
  labelB: string;
  isLoading: boolean;
  invertColor?: boolean;
}

const ComparisonSummaryCard: React.FC<ComparisonSummaryCardProps> = ({
  label,
  amountA,
  amountB,
  currency,
  labelA,
  labelB,
  isLoading,
  invertColor = false,
}) => {
  const delta = calculatePeriodDelta(amountA, amountB);

  return (
    <MainCard>
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      {isLoading ? (
        <Skeleton variant="text" width="80%" height={40} />
      ) : (
        <>
          <Stack direction="row" alignItems="baseline" flexWrap="wrap" sx={{ mt: 0.5 }}>
            <Typography variant="h4" fontWeight={600}>
              {currency} {amountA.toFixed(2)}
            </Typography>
            <DeltaBadge delta={delta} invertColor={invertColor} />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {labelA}: {currency} {amountA.toFixed(2)} · {labelB}: {currency} {amountB.toFixed(2)}
          </Typography>
        </>
      )}
    </MainCard>
  );
};

interface PeriodComparisonSummaryCardsProps {
  totalIncomeA: number;
  totalIncomeB: number;
  totalExpensesA: number;
  totalExpensesB: number;
  currency: string;
  isLoading: boolean;
  labelA: string;
  labelB: string;
}

const PeriodComparisonSummaryCards: React.FC<PeriodComparisonSummaryCardsProps> = ({
  totalIncomeA,
  totalIncomeB,
  totalExpensesA,
  totalExpensesB,
  currency,
  isLoading,
  labelA,
  labelB,
}) => {
  const { t } = useTranslation();
  const netBalanceA = totalIncomeA - totalExpensesA;
  const netBalanceB = totalIncomeB - totalExpensesB;

  return (
    <Grid container spacing={2} mb={2}>
      <Grid item xs={12} sm={4}>
        <ComparisonSummaryCard
          label={t('report.total_income')}
          amountA={totalIncomeA}
          amountB={totalIncomeB}
          currency={currency}
          labelA={labelA}
          labelB={labelB}
          isLoading={isLoading}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <ComparisonSummaryCard
          label={t('report.total_expenses')}
          amountA={totalExpensesA}
          amountB={totalExpensesB}
          currency={currency}
          labelA={labelA}
          labelB={labelB}
          isLoading={isLoading}
          invertColor
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <ComparisonSummaryCard
          label={t('report.net_balance')}
          amountA={netBalanceA}
          amountB={netBalanceB}
          currency={currency}
          labelA={labelA}
          labelB={labelB}
          isLoading={isLoading}
        />
      </Grid>
    </Grid>
  );
};

export default PeriodComparisonSummaryCards;
